import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const hStyle: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontWeight: 800,
  color: 'var(--bw-dark)',
  letterSpacing: '-0.025em',
  lineHeight: 1.15,
};

const Team = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bw-white)' }}>
      <Navigation />

      {/* Hero */}
      <section className="bw-hero">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-3xl mx-auto text-center">
            <div className="bw-eyebrow-pill">Who We Are</div>
            <h1 style={{ ...hStyle, fontSize: 'clamp(36px, 5.5vw, 68px)', marginBottom: '20px' }}>
              Built by someone who's{' '}
              <em style={{ fontStyle: 'normal', color: 'var(--bw-blue)' }}>been there.</em>
            </h1>
            <p style={{ fontSize: 'clamp(16px, 2vw, 20px)', color: 'var(--bw-text-mid)', lineHeight: 1.7, maxWidth: '520px', margin: '0 auto' }}>
              BrightWings is a small, deliberate team. We don't scale headcount for its own sake — we bring the right experience to the right problem.
            </p>
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="bw-section-white">
        <div className="max-w-5xl mx-auto">
          <div
            className="bw-card"
            style={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              gap: '48px',
              alignItems: 'center',
              padding: '48px',
            }}
          >
            {/* Photo */}
            <div style={{ flexShrink: 0 }}>
              <div
                style={{
                  width: '180px',
                  height: '180px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  border: '3px solid var(--bw-blue)',
                  boxShadow: '0 0 0 6px rgba(0, 163, 224, 0.12)',
                  background: 'linear-gradient(135deg, rgba(0,163,224,0.15) 0%, rgba(0,163,224,0.05) 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img
                  src="/team/isaac.jpg"
                  alt="Isaac Musselwhite"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => {
                    // Fallback to initials if image not found
                    const target = e.currentTarget;
                    target.style.display = 'none';
                    const parent = target.parentElement;
                    if (parent) {
                      parent.innerHTML = `<span style="font-family: var(--font-display); font-weight: 800; font-size: 52px; color: var(--bw-blue); letter-spacing: -0.02em;">IM</span>`;
                    }
                  }}
                />
              </div>
            </div>

            {/* Bio */}
            <div>
              <div style={{ marginBottom: '6px' }}>
                <span
                  style={{
                    fontSize: '12px',
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: 'var(--bw-blue)',
                    fontFamily: 'var(--font-display)',
                  }}
                >
                  Founder
                </span>
              </div>
              <h2 style={{ ...hStyle, fontSize: 'clamp(24px, 3vw, 36px)', marginBottom: '20px' }}>
                Isaac Musselwhite
              </h2>
              <p style={{ fontSize: '18px', lineHeight: 1.8, color: 'var(--bw-text-mid)', marginBottom: '16px' }}>
                Isaac founded BrightWings after watching talented founders stall mid-build — not from lack of vision, but lack of direction.
              </p>
              <p style={{ fontSize: '18px', lineHeight: 1.8, color: 'var(--bw-text-mid)', marginBottom: '16px' }}>
                His time at Block gave him a front-row view of what it takes to scale software inside a growth-stage company. Now he brings that experience directly to founders who need clear decisions before code.
              </p>
              <div style={{ marginTop: '28px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <a
                  href="https://www.linkedin.com/in/isaac-musselwhite-27a7a730/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bw-btn-secondary"
                  style={{ fontSize: '14px', padding: '10px 22px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                  </svg>
                  LinkedIn
                </a>
              </div>
            </div>
          </div>

          {/* Mobile layout override */}
          <style>{`
            @media (max-width: 640px) {
              .team-founder-card {
                grid-template-columns: 1fr !important;
                text-align: center;
              }
              .team-founder-card > div:first-child {
                display: flex;
                justify-content: center;
              }
              .team-founder-card .btn-row {
                justify-content: center;
              }
            }
          `}</style>
        </div>
      </section>

      {/* Philosophy */}
      <section className="bw-section-surface">
        <div className="max-w-5xl mx-auto">
          <div className="max-w-3xl">
            <h2 style={{ ...hStyle, fontSize: 'clamp(24px, 3vw, 38px)', marginBottom: '20px' }}>
              Why small is a feature, not a limitation.
            </h2>
            <p style={{ fontSize: '18px', lineHeight: 1.8, color: 'var(--bw-text-mid)', marginBottom: '20px' }}>
              Most agencies grow to fill capacity. We grow to maintain quality. Every engagement gets senior attention because there's no junior layer between you and the person who understands the problem.
            </p>
            <p style={{ fontSize: '18px', lineHeight: 1.8, color: 'var(--bw-text-mid)' }}>
              When the scope grows, we bring in trusted collaborators — not warm bodies. People we've worked with, whose judgment we trust. The standard doesn't change based on how big the project is.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bw-cta-section">
        <div className="max-w-4xl mx-auto">
          <h2 style={{ ...hStyle, fontSize: 'clamp(28px, 3.5vw, 44px)', marginBottom: '16px' }}>
            Ready to work together?
          </h2>
          <p style={{ fontSize: '18px', color: 'var(--bw-text-mid)', marginBottom: '36px', lineHeight: 1.7 }}>
            Let's have a real conversation about what you're building and whether it makes sense to work together.
          </p>
          <Link to="/contact" onClick={() => window.scrollTo(0, 0)} className="bw-btn-primary">
            Start a conversation
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Team;
